import React, { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MarkdownRenderer from "../../MarkdownRenderer";
import styles from "../agent-response.module.scss";

type RfqOrchestratorAgentProps = {
  parsedContent: any;
};

// Accordion component for each criterion
const CriterionAccordion = ({
  criterion,
  index,
}: {
  criterion: any;
  index: number;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(criterion.description || "");
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const getPriorityStyle = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case "mandatory":
        return { bg: "#ffebee", color: "#c62828" };
      case "important":
        return { bg: "#fff3e0", color: "#e65100" };
      default:
        return { bg: "#e3f2fd", color: "#1565c0" };
    }
  };

  const priorityStyle = getPriorityStyle(criterion.priority);

  return (
    <div
      style={{
        border: "1px solid #b2e5de",
        borderRadius: "8px",
        marginBottom: "16px",
        overflow: "hidden",
        backgroundColor: "#fff",
      }}
    >
      <div
        style={{
          background: "#f0fbf9",
          padding: "16px 20px",
          cursor: "pointer",
          transition: "background 0.2s",
        }}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "#f0fbf9")}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
              minWidth: 0,
            }}
          >
            {isOpen ? (
              <ExpandLessIcon htmlColor="#006d77" sx={{ fontSize: "24px" }} />
            ) : (
              <ExpandMoreIcon htmlColor="#006d77" sx={{ fontSize: "24px" }} />
            )}

            <Chip
              label={criterion.criterion_id || `C-${index + 1}`}
              size="small"
              sx={{
                backgroundColor: "#f0fbf9",
                color: "#004141",
                fontWeight: 500,
                fontSize: "11px",
                height: "22px",
                border: "1px solid #b2e5de",
              }}
            />

            <h3
              style={{
                fontSize: "16px",
                fontWeight: 500,
                color: "#004141",
                margin: 0,
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {criterion.criterion_name || "Evaluation Criterion"}
            </h3>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {criterion.priority && (
              <Chip
                label={criterion.priority}
                size="small"
                sx={{
                  height: "22px",
                  fontSize: "11px",
                  fontWeight: 500,
                  backgroundColor: priorityStyle.bg,
                  color: priorityStyle.color,
                  borderRadius: "12px",
                  padding: "4px 12px",
                }}
              />
            )}

            <div
              style={{
                backgroundColor: "#004141",
                color: "#fff",
                fontSize: "12px",
                fontWeight: 500,
                padding: "6px 12px",
                borderRadius: "6px",
                minWidth: "50px",
                textAlign: "center",
              }}
            >
              {criterion.weight ? `${criterion.weight}%` : "N/A"}
            </div>
          </div>
        </div>
      </div>

      {isOpen && (
        <div
          style={{
            padding: "20px",
            background: "#ffffff",
            position: "relative",
          }}
        >
          <Box sx={{ position: "absolute", top: "12px", right: "12px" }}>
            <Tooltip title={copySuccess ? "Copied!" : "Copy Description"}>
              <IconButton
                size="small"
                onClick={handleCopy}
                sx={{
                  padding: "3px",
                  minWidth: "28px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f3f4f6",
                  },
                }}
              >
                <ContentCopyIcon
                  style={{
                    color: copySuccess ? "#00a888" : "#9ca3af",
                    fontSize: "20px",
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>

          <div style={{ paddingRight: "40px" }}>
            {criterion.category && (
              <Box sx={{ marginBottom: 2 }}>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#004141",
                    marginBottom: "6px",
                  }}
                >
                  Category
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#2a312b",
                    lineHeight: 1.5,
                  }}
                >
                  {criterion.category}
                </Typography>
              </Box>
            )}

            {criterion.description && (
              <Box>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#004141",
                    marginBottom: "6px",
                  }}
                >
                  Description
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#2a312b",
                    lineHeight: 1.6,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {criterion.description}
                </Typography>
              </Box>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function RfqOrchestratorAgent({
  parsedContent,
}: RfqOrchestratorAgentProps): JSX.Element {
  console.log("RFQ Orchestrator Agent - Parsed Content:", parsedContent);
  console.log(
    "RFQ Orchestrator Agent - Full Structure:",
    JSON.stringify(parsedContent, null, 2),
  );

  // Extract data - handle multiple possible structures
  // 1. Direct: parsedContent itself is the result
  // 2. Wrapped in finalAnswer: parsedContent.finalAnswer is the result (most common for streaming)
  // 3. Wrapped in result: parsedContent.result is the result
  // 4. SSE payload structure: parsedContent.result.data (from userSlice lastCompletedPayload)

  let outerResult =
    parsedContent?.finalAnswer || parsedContent?.result || parsedContent;

  // If finalAnswer is a string, try to parse it
  if (typeof outerResult === "string") {
    try {
      outerResult = JSON.parse(outerResult);
    } catch (e) {
      console.error("Failed to parse finalAnswer string:", e);
    }
  }

  // Handle SSE payload structure: parsedContent.result.data (from userSlice lastCompletedPayload)
  // For question_answered: payload.result.data contains the orchestrator response with nested response array
  const dataFromPayload = parsedContent?.result?.data;
  if (dataFromPayload) {
    outerResult = dataFromPayload;
  }

  const message = outerResult?.message || outerResult?.question || "";

  // Extract status from outerResult (rfq_orchestrator preserves status at top level)
  let status = outerResult?.status || parsedContent?.status || "";

  // Handle Q&A responses - extract from response array preserved by userSlice
  // For RFQ orchestrator Q&A, the response array is at outerResult.response
  let qaResponse = null;
  if (outerResult?.response && Array.isArray(outerResult.response)) {
    console.log("Found response array:", outerResult.response);
    const qaAgent = outerResult.response.find(
      (r: any) => r.agent_id === "rfq_qa_agent",
    );
    if (qaAgent?.result) {
      qaResponse = qaAgent.result;
      console.log("Found QA response:", qaResponse);
    }
  }

  // If we have a QA response, ensure status is set to question_answered
  if (qaResponse && status === "question_answered") {
    console.log("Confirmed QA response display with status:", status);
  }

  // Extract RFQ-specific data from nested result or response array
  let rfqData = outerResult?.result || outerResult;

  // Handle response array structure: data.response[0].result
  if (outerResult?.response && Array.isArray(outerResult.response)) {
    // Try criteria_parser first (awaiting_confirmation flow)
    const criteriaParserResult = outerResult.response.find(
      (r: any) => r.agent_id === "criteria_parser",
    );
    if (criteriaParserResult?.result) {
      rfqData = criteriaParserResult.result;
    }

    // For criteria_parsed status, check rfq_orchestrator.result.result
    const orchestratorResult = outerResult.response.find(
      (r: any) => r.agent_id === "rfq_orchestrator",
    );
    if (orchestratorResult?.result?.result) {
      rfqData = orchestratorResult.result.result;
    }
  }

  const rfqName = rfqData?.rfq_name || "";
  const rfqReference = rfqData?.rfq_reference || "";
  const totalTechnicalScore = rfqData?.total_technical_score || 0;
  const overallThreshold = rfqData?.overall_threshold || 0;
  const criteria = rfqData?.criteria || [];
  const nextAction = outerResult?.next_action || "";

  // Extract question data for asking_initial_questions status
  const currentQuestion = outerResult?.current_question || null;
  const totalQuestions = outerResult?.total_questions || 0;
  const answeredQuestions = outerResult?.answered_questions || 0;

  console.log("RFQ Orchestrator Agent - Extracted Data:", {
    message,
    status,
    rfqName,
    criteriaCount: criteria.length,
    nextAction,
    qaResponse,
    currentQuestion,
    totalQuestions,
    answeredQuestions,
  });

  return (
    <div className={styles.pressReleaseContent}>
      {/* Asking Initial Questions Status */}
      {status === "asking_initial_questions" && currentQuestion && (
        <Paper
          elevation={0}
          sx={{
            padding: "20px",
            marginBottom: "20px",
            backgroundColor: "#fff",
            border: "1px solid #b2e5de",
            borderRadius: "8px",
          }}
        >
          {/* Agent Message */}
          {message && (
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#2a312b",
                  lineHeight: 1.6,
                }}
              >
                {message}
              </Typography>
            </Box>
          )}

          {/* Current Question */}
          <Box
            sx={{
              padding: "16px",
              backgroundColor: "#f0fbf9",
              borderRadius: "6px",
              border: "1px solid #b2e5de",
            }}
          >
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 500,
                color: "#004141",
                marginBottom: currentQuestion.help_text ? "8px" : "0",
                lineHeight: 1.5,
              }}
            >
              {currentQuestion.question_text}
              {currentQuestion.is_required && (
                <span style={{ color: "#bd1919", marginLeft: "4px" }}>*</span>
              )}
            </Typography>

            {/* Help Text */}
            {currentQuestion.help_text && (
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "#2a312b",
                  fontStyle: "italic",
                  opacity: 0.8,
                }}
              >
                {currentQuestion.help_text}
              </Typography>
            )}

            {/* Options (if question has predefined options) */}
            {currentQuestion.options && currentQuestion.options.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#004141",
                    marginBottom: "8px",
                  }}
                >
                  Available options:
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {currentQuestion.options.map((option: string, idx: number) => (
                    <Box
                      key={idx}
                      sx={{
                        padding: "10px 12px",
                        backgroundColor: "#fff",
                        border: "1px solid #b2e5de",
                        borderRadius: "6px",
                        fontSize: "13px",
                        color: "#2a312b",
                        lineHeight: 1.5,
                      }}
                    >
                      • {option}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* Instruction */}
          <Box
            sx={{
              marginTop: "16px",
              padding: "12px 16px",
              backgroundColor: "#fafafa",
              border: "1px solid #b2e5de",
              borderRadius: "6px",
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                color: "#004141",
                fontWeight: 500,
              }}
            >
              Please type your answer in the chat input below and send
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Awaiting Confirmation Status */}
      {status === "awaiting_confirmation" && message && (
        <Paper
          elevation={0}
          sx={{
            padding: "16px",
            marginBottom: "20px",
            backgroundColor: "#f0fbf9",
            border: "1px solid #b2e5de",
            borderRadius: "6px",
          }}
        >
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: 500,
              color: "#004141",
              marginBottom: "12px",
            }}
          >
            Confirmation Required
          </Typography>
          <Box
            sx={{
              fontSize: "14px",
              color: "#2a312b",
              lineHeight: 1.6,
              "& p": { margin: 0 },
            }}
          >
            <MarkdownRenderer content={message} />
          </Box>
        </Paper>
      )}

      {/* Awaiting Scoring Document Status */}
      {status === "awaiting_scoring_doc" && message && (
        <Paper
          elevation={0}
          sx={{
            padding: "16px",
            marginBottom: "20px",
            backgroundColor: "#f0fbf9",
            border: "1px solid #b2e5de",
            borderRadius: "6px",
          }}
        >
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: 500,
              color: "#004141",
              marginBottom: "12px",
            }}
          >
            Scoring Document Request
          </Typography>
          <Box
            sx={{
              fontSize: "14px",
              color: "#2a312b",
              lineHeight: 1.6,
              "& p": { margin: 0 },
            }}
          >
            <MarkdownRenderer content={message} />
          </Box>
        </Paper>
      )}

      {/* Criteria Parsed Status */}
      {status === "criteria_parsed" && message && (
        <Paper
          elevation={0}
          sx={{
            padding: "16px",
            marginBottom: "20px",
            backgroundColor: "#f0fbf9",
            border: "1px solid #b2e5de",
            borderRadius: "6px",
          }}
        >
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: 500,
              color: "#004141",
              marginBottom: "12px",
            }}
          >
            Criteria Ready
          </Typography>
          <Box
            sx={{
              fontSize: "14px",
              color: "#2a312b",
              lineHeight: 1.6,
              "& p": { margin: 0 },
            }}
          >
            <MarkdownRenderer content={message} />
          </Box>
        </Paper>
      )}

      {/* Question Answered Status */}
      {status === "question_answered" && qaResponse && (
        <Paper
          elevation={0}
          sx={{
            padding: "20px",
            marginBottom: "20px",
            backgroundColor: "#fff",
            border: "1px solid #b2e5de",
            borderRadius: "8px",
          }}
        >
          {/* Answer */}
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#004141",
                mb: 1.5,
              }}
            >
              Answer
            </Typography>
            <Box
              sx={{
                fontSize: "14px",
                color: "#2a312b",
                lineHeight: 1.6,
                "& p": { marginBottom: "12px" },
                "& ul, & ol": { marginLeft: "20px", marginBottom: "12px" },
                "& li": { marginBottom: "8px" },
              }}
            >
              <MarkdownRenderer content={qaResponse.answer || ""} />
            </Box>
          </Box>

          {/* Evidence References */}
          {qaResponse.evidence_references &&
            qaResponse.evidence_references.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#004141",
                    mb: 1.5,
                  }}
                >
                  Evidence References
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {qaResponse.evidence_references.map(
                    (ref: string, idx: number) => (
                      <Box
                        key={idx}
                        sx={{
                          padding: "12px",
                          backgroundColor: "#f0fbf9",
                          border: "1px solid #b2e5de",
                          borderRadius: "6px",
                          fontSize: "13px",
                          color: "#2a312b",
                          lineHeight: 1.5,
                        }}
                      >
                        {ref}
                      </Box>
                    ),
                  )}
                </Box>
              </Box>
            )}

          {/* Confidence Level */}
          {qaResponse.confidence && (
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#004141",
                  mb: 1,
                }}
              >
                Confidence Level
              </Typography>
              <Chip
                label={qaResponse.confidence}
                size="small"
                sx={{
                  backgroundColor:
                    qaResponse.confidence.toLowerCase() === "high"
                      ? "#e8f5e9"
                      : qaResponse.confidence.toLowerCase() === "medium"
                        ? "#fff3e0"
                        : "#ffebee",
                  color:
                    qaResponse.confidence.toLowerCase() === "high"
                      ? "#2e7d32"
                      : qaResponse.confidence.toLowerCase() === "medium"
                        ? "#e65100"
                        : "#bd1919",
                  fontWeight: 500,
                  fontSize: "12px",
                  height: "24px",
                  padding: "4px 12px",
                  borderRadius: "12px",
                }}
              />
            </Box>
          )}

          {/* Follow-up Suggestions */}
          {qaResponse.follow_up_suggestions &&
            qaResponse.follow_up_suggestions.length > 0 && (
              <Box>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#004141",
                    mb: 1.5,
                  }}
                >
                  Follow-up Suggestions
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {qaResponse.follow_up_suggestions.map(
                    (suggestion: string, idx: number) => (
                      <Box
                        key={idx}
                        sx={{
                          padding: "12px",
                          backgroundColor: "#fafafa",
                          border: "1px solid #b2e5de",
                          borderRadius: "6px",
                          fontSize: "13px",
                          color: "#2a312b",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "8px",
                          lineHeight: 1.5,
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#004141",
                            fontWeight: 500,
                            fontSize: "13px",
                          }}
                        >
                          {idx + 1}.
                        </Typography>
                        <Typography
                          sx={{ fontSize: "13px", color: "#2a312b", flex: 1 }}
                        >
                          {suggestion}
                        </Typography>
                      </Box>
                    ),
                  )}
                </Box>
              </Box>
            )}
        </Paper>
      )}

      {/* Regular Message (non-special states) */}
      {message &&
        status !== "asking_initial_questions" &&
        status !== "awaiting_confirmation" &&
        status !== "awaiting_scoring_doc" &&
        status !== "criteria_parsed" &&
        status !== "question_answered" && (
          <Box sx={{ marginBottom: 3 }}>
            <MarkdownRenderer content={message} />
          </Box>
        )}

      {/* Show criteria when available */}
      {criteria.length > 0 && (
        <>
          {/* Header Summary */}
          <Paper
            elevation={0}
            sx={{
              padding: "20px",
              marginBottom: "20px",
              backgroundColor: "#f0fbf9",
              border: "1px solid #b2e5de",
              borderRadius: "8px",
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{ fontSize: "16px", fontWeight: 500, color: "#004141" }}
              >
                {rfqName || "RFQ Evaluation"}
              </Typography>
            </Box>

            <Box
              component="table"
              sx={{
                width: "100%",
                borderCollapse: "collapse",
                "& td": {
                  padding: "10px 12px",
                  fontSize: "14px",
                  borderBottom: "1px solid #b2e5de",
                },
                "& tr:last-child td": {
                  borderBottom: "none",
                },
              }}
            >
              <tbody>
                {rfqReference && rfqReference !== "N/A" && (
                  <tr>
                    <td
                      style={{
                        color: "#2a312b",
                        fontWeight: 500,
                        width: "40%",
                      }}
                    >
                      Reference
                    </td>
                    <td style={{ color: "#004141", fontWeight: 500 }}>
                      {rfqReference}
                    </td>
                  </tr>
                )}
                <tr>
                  <td
                    style={{ color: "#2a312b", fontWeight: 500, width: "40%" }}
                  >
                    Total Technical Score
                  </td>
                  <td style={{ color: "#004141", fontWeight: 500 }}>
                    {totalTechnicalScore}
                  </td>
                </tr>
                {overallThreshold > 0 && (
                  <tr>
                    <td
                      style={{
                        color: "#2a312b",
                        fontWeight: 500,
                        width: "40%",
                      }}
                    >
                      Overall Threshold
                    </td>
                    <td style={{ color: "#004141", fontWeight: 500 }}>
                      {overallThreshold}
                    </td>
                  </tr>
                )}
                <tr>
                  <td
                    style={{ color: "#2a312b", fontWeight: 500, width: "40%" }}
                  >
                    Criteria Count
                  </td>
                  <td style={{ color: "#004141", fontWeight: 500 }}>
                    {criteria.length}
                  </td>
                </tr>
                {criteria.length > 0 && criteria[0].scoring_scale && (
                  <tr>
                    <td
                      style={{
                        color: "#2a312b",
                        fontWeight: 500,
                        width: "40%",
                      }}
                    >
                      Scoring Scale
                    </td>
                    <td style={{ color: "#004141", fontWeight: 500 }}>
                      {criteria[0].scoring_scale}
                    </td>
                  </tr>
                )}
              </tbody>
            </Box>
          </Paper>

          {/* Criteria Accordions */}
          <Box sx={{ marginBottom: 3 }}>
            {criteria.map((criterion: any, index: number) => (
              <CriterionAccordion
                key={index}
                criterion={criterion}
                index={index}
              />
            ))}
          </Box>

          {/* Next Action */}
          {nextAction && status && (
            <Paper
              elevation={0}
              sx={{
                padding: "16px",
                backgroundColor: "#f0fbf9",
                border: "1px solid #b2e5de",
                borderRadius: "6px",
              }}
            >
              <Typography sx={{ fontSize: "14px", color: "#2a312b" }}>
                <strong style={{ color: "#004141", fontWeight: 500 }}>
                  Next Action:
                </strong>{" "}
                {nextAction
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase())}
              </Typography>
            </Paper>
          )}
        </>
      )}

      {/* Completed Status */}
      {status === "completed" && (
        <Box sx={{ marginBottom: 3 }}>
          <Paper
            elevation={0}
            sx={{
              padding: "20px",
              backgroundColor: "#f0fbf9",
              border: "1px solid #b2e5de",
              borderRadius: "8px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <CheckCircleIcon sx={{ fontSize: 24, color: "#004141" }} />
              <Typography
                sx={{ fontSize: "16px", fontWeight: 500, color: "#004141" }}
              >
                RFQ Evaluation Completed
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Fallback */}
      {!message && criteria.length === 0 && !status && (
        <Box sx={{ marginBottom: 3 }}>
          <Typography
            sx={{
              padding: "24px",
              textAlign: "center",
              color: "#2a312b",
              fontSize: "16px",
              fontStyle: "italic",
            }}
          >
            Processing RFQ document...
          </Typography>
        </Box>
      )}
    </div>
  );
}
